import { useState, useMemo, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import { API_BASE } from "../../config/apiBase";
import "../../styles/pages/content/_administration-security.scss";

const SEVERITY_META = {
    critical: { label: "Critical", mod: "adm-security-pill--critical" },
    warn: { label: "Warning", mod: "adm-security-pill--warn" },
    info: { label: "Info", mod: "adm-security-pill--info" },
};

const AUDIT_PAGE_SIZE = 6;

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtTs(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString([], {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function exportToCsv(rows, filename) {
    const headers = ["Timestamp", "Actor", "Action", "Target", "Severity"];
    const lines = [
        headers,
        ...rows.map((r) => [
            fmtTs(r.ts),
            r.actor,
            r.action,
            r.target,
            r.severity,
        ]),
    ];
    const csv = lines.map((r) => r.join(",")).join("\n");
    const a = Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
        download: filename,
    });
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

function fmtRelative(iso) {
    if (!iso) return "—";
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return "—";
    const diffMs = Date.now() - dt.getTime();
    if (diffMs < 60 * 1000) return "Just now";
    const diffMins = Math.round(diffMs / (60 * 1000));
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.round(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hr ago`;
    const diffDays = Math.round(diffHours / 24);
    return `${diffDays} days ago`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function SectionHead({ iconMod, title, pill, children }) {
    return (
        <div className="adm-security-section-head">
            <div className="adm-security-section-head__left">
                {iconMod && (
                    <span
                        className={`adm-security-section-head__icon adm-security-section-head__icon--${iconMod}`}
                        aria-hidden="true"
                    />
                )}
                <span className="adm-security-section-head__title">
                    {title}
                </span>
                {pill && (
                    <span className={`adm-security-pill ${pill.mod}`}>
                        {pill.label}
                    </span>
                )}
            </div>
            {children && (
                <div className="adm-security-section-head__right">
                    {children}
                </div>
            )}
        </div>
    );
}

function StatCard({ value, label, sub, accent }) {
    return (
        <div
            className={`adm-security-stat adm-security-stat--${accent || "default"}`}
        >
            <span className="adm-security-stat__value">{value}</span>
            <span className="adm-security-stat__label">{label}</span>
            {sub && <span className="adm-security-stat__sub">{sub}</span>}
        </div>
    );
}

function SecurityChecksPanel({
    checks,
    loading,
    title = "Security health",
    iconMod = "shield",
    ariaLabel = "Security health checks",
}) {
    const hasChecks = checks.length > 0;
    const passCount = checks.filter((c) => c.status === "pass").length;
    const warnCount = checks.filter((c) => c.status === "warn").length;

    return (
        <section className="adm-security-panel" aria-label={ariaLabel}>
            <SectionHead
                iconMod={iconMod}
                title={title}
                pill={
                    hasChecks
                        ? {
                              label: `${passCount}/${checks.length} passing`,
                              mod:
                                  warnCount > 0
                                      ? "adm-security-pill--warn"
                                      : "adm-security-pill--pass",
                          }
                        : {
                              label: "No checks yet",
                              mod: "adm-security-pill--info",
                          }
                }
            />
            {loading ? (
                <p className="adm-security-checks-empty">
                    Loading security checks…
                </p>
            ) : hasChecks ? (
                <ul className="adm-security-checks" role="list">
                    {checks.map((check, i) => {
                        const isWarn = check.status === "warn";
                        return (
                            <li
                                key={check.id}
                                className="adm-security-check"
                                style={{ "--i": i }}
                            >
                                <span
                                    className={`adm-security-check__icon adm-security-check__icon--${check.status}`}
                                    aria-hidden="true"
                                />
                                <div className="adm-security-check__body">
                                    <span className="adm-security-check__label">
                                        {check.label}
                                    </span>
                                    <span className="adm-security-check__detail">
                                        {check.detail}
                                    </span>
                                </div>
                                <span
                                    className={`adm-security-pill ${isWarn ? "adm-security-pill--warn" : "adm-security-pill--pass"}`}
                                >
                                    {isWarn ? "Review" : "Pass"}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <p className="adm-security-checks-empty">
                    No security checks available yet.
                </p>
            )}
        </section>
    );
}

function ActiveSessionsPanel({ sessions }) {
    return (
        <section className="adm-security-panel" aria-label="Active sessions">
            <SectionHead
                iconMod="activity"
                title="Active sessions"
                pill={{
                    label: `${sessions.length} online`,
                    mod: "adm-security-pill--info",
                }}
            >
                <button
                    type="button"
                    className="adm-security-btn adm-security-btn--ghost adm-security-btn--sm"
                >
                    Revoke all others
                </button>
            </SectionHead>
            {sessions.length === 0 ? (
                <p className="adm-security-sessions-empty">
                    No active sessions in the last 24 hours.
                </p>
            ) : null}
            <ul className="adm-security-sessions" role="list">
                {sessions.map((session) => (
                    <li
                        key={session.id}
                        className={`adm-security-session${session.current ? " adm-security-session--current" : ""}`}
                    >
                        <div className="adm-security-session__left">
                            <span
                                className="adm-security-session__dot"
                                aria-hidden="true"
                            />
                            <div className="adm-security-session__text">
                                <span className="adm-security-session__user">
                                    {session.user}
                                </span>
                                <span className="adm-security-session__meta">
                                    {session.device}
                                    {session.location
                                        ? ` · ${session.location}`
                                        : ""}
                                </span>
                            </div>
                        </div>
                        <div className="adm-security-session__right">
                            <span className="adm-security-session__seen">
                                {session.lastSeen}
                            </span>
                            {session.current ? (
                                <span className="adm-security-pill adm-security-pill--pass">
                                    Current
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    className="adm-security-revoke-btn"
                                >
                                    Revoke
                                </button>
                            )}
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function AuditLogPanel({ log, loading, onSelect }) {
    const [severityFilter, setSeverityFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [showAll, setShowAll] = useState(false);

    const filtered = useMemo(() => {
        let rows = log;
        if (severityFilter !== "all")
            rows = rows.filter((r) => r.severity === severityFilter);
        if (query.trim()) {
            const q = query.trim().toLowerCase();
            rows = rows.filter(
                (r) =>
                    r.actor.toLowerCase().includes(q) ||
                    r.action.toLowerCase().includes(q) ||
                    r.target.toLowerCase().includes(q)
            );
        }
        return showAll ? rows : rows.slice(0, AUDIT_PAGE_SIZE);
    }, [log, severityFilter, query, showAll]);

    const canShowMore =
        !showAll &&
        filtered.length === AUDIT_PAGE_SIZE &&
        log.length > AUDIT_PAGE_SIZE;

    return (
        <section
            className="adm-security-panel adm-security-panel--flush"
            aria-label="Audit log"
        >
            <SectionHead
                iconMod="key"
                title="Audit log"
                pill={{
                    label: `${log.length} events`,
                    mod: "adm-security-pill--info",
                }}
            >
                <div className="adm-security-audit-filters">
                    <label className="adm-security-search-wrap">
                        <span
                            className="adm-security-search-wrap__icon"
                            aria-hidden="true"
                        />
                        <input
                            type="search"
                            className="adm-security-search"
                            placeholder="Search events…"
                            aria-label="Search audit log"
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setShowAll(false);
                            }}
                        />
                    </label>
                    <select
                        className="adm-security-select"
                        value={severityFilter}
                        aria-label="Filter by severity"
                        onChange={(e) => {
                            setSeverityFilter(e.target.value);
                            setShowAll(false);
                        }}
                    >
                        <option value="all">All severities</option>
                        <option value="critical">Critical</option>
                        <option value="warn">Warning</option>
                        <option value="info">Info</option>
                    </select>
                </div>
            </SectionHead>

            <div
                className="adm-security-audit-head"
                role="row"
                aria-label="Column headers"
            >
                <span role="columnheader">Timestamp</span>
                <span role="columnheader">Actor</span>
                <span role="columnheader">Action</span>
                <span role="columnheader">Target</span>
                <span role="columnheader">Severity</span>
            </div>

            <div
                className="adm-security-audit-body"
                role="table"
                aria-label="Audit events"
            >
                {filtered.length > 0 ? (
                    filtered.map((entry, i) => {
                        const meta =
                            SEVERITY_META[entry.severity] ?? SEVERITY_META.info;
                        return (
                            <div
                                key={entry.id}
                                className="adm-security-audit-row"
                                role="row"
                                tabIndex={0}
                                style={{ "--i": i }}
                                onClick={() => onSelect?.(entry)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        onSelect?.(entry);
                                    }
                                }}
                            >
                                <span className="adm-security-audit-row__ts">
                                    {fmtTs(entry.ts)}
                                </span>
                                <span className="adm-security-audit-row__actor">
                                    {entry.actor}
                                </span>
                                <span className="adm-security-audit-row__action">
                                    {entry.action}
                                </span>
                                <span className="adm-security-audit-row__target">
                                    {entry.target}
                                </span>
                                <span
                                    className={`adm-security-pill ${meta.mod}`}
                                >
                                    {meta.label}
                                </span>
                            </div>
                        );
                    })
                ) : loading ? (
                    <p className="adm-security-audit-empty">
                        Loading audit log…
                    </p>
                ) : (
                    <p className="adm-security-audit-empty">
                        No events match your filters.
                    </p>
                )}
            </div>

            {canShowMore && (
                <div className="adm-security-audit-footer">
                    <button
                        type="button"
                        className="adm-security-btn adm-security-btn--ghost adm-security-btn--sm"
                        onClick={() => setShowAll(true)}
                    >
                        Show all {log.length} events
                        <span
                            className="adm-security-btn__icon adm-security-btn__icon--chevron"
                            aria-hidden="true"
                        />
                    </button>
                </div>
            )}
        </section>
    );
}

function AuditLogModal({ entry, onClose }) {
    useEffect(() => {
        if (!entry) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [entry, onClose]);

    if (!entry || typeof document === "undefined") return null;

    const meta = SEVERITY_META[entry.severity] ?? SEVERITY_META.info;

    return createPortal(
        <div className="adm-security-modal" role="dialog" aria-modal="true">
            <div
                className="adm-security-modal__backdrop"
                onClick={onClose}
                aria-hidden="true"
            />
            <div className="adm-security-modal__card">
                <div className="adm-security-modal__head">
                    <p className="adm-security-modal__title">Audit detail</p>
                    <span className={`adm-security-pill ${meta.mod}`}>
                        {meta.label}
                    </span>
                </div>
                <p className="adm-security-modal__sub">
                    {entry.action || "Audit event"}
                </p>
                <div className="adm-security-modal__grid">
                    <div className="adm-security-modal__row">
                        <span className="adm-security-modal__label">
                            Timestamp
                        </span>
                        <span className="adm-security-modal__value">
                            {fmtTs(entry.ts)}
                        </span>
                    </div>
                    <div className="adm-security-modal__row">
                        <span className="adm-security-modal__label">Actor</span>
                        <span className="adm-security-modal__value">
                            {entry.actor}
                        </span>
                    </div>
                    <div className="adm-security-modal__row">
                        <span className="adm-security-modal__label">
                            Target
                        </span>
                        <span className="adm-security-modal__value">
                            {entry.target}
                        </span>
                    </div>
                    {entry.id && (
                        <div className="adm-security-modal__row">
                            <span className="adm-security-modal__label">
                                Event ID
                            </span>
                            <span className="adm-security-modal__value">
                                {entry.id}
                            </span>
                        </div>
                    )}
                </div>
                <pre className="adm-security-modal__raw">
                    {JSON.stringify(entry, null, 2)}
                </pre>
                <div className="adm-security-modal__actions">
                    <button
                        type="button"
                        className="adm-security-btn adm-security-btn--ghost adm-security-btn--sm"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function ProcessingErrorModal({ entry, onClose }) {
    useEffect(() => {
        if (!entry) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [entry, onClose]);

    if (!entry || typeof document === "undefined") return null;

    const timestamp = entry.error_at || entry.created_at;

    return createPortal(
        <div className="adm-security-modal" role="dialog" aria-modal="true">
            <div
                className="adm-security-modal__backdrop"
                onClick={onClose}
                aria-hidden="true"
            />
            <div className="adm-security-modal__card">
                <div className="adm-security-modal__head">
                    <p className="adm-security-modal__title">
                        Processing error
                    </p>
                    <span className="adm-security-pill adm-security-pill--critical">
                        {entry.type || "Error"}
                    </span>
                </div>
                <p className="adm-security-modal__sub">
                    {entry.message || "Processing error"}
                </p>
                <div className="adm-security-modal__grid">
                    {timestamp && (
                        <div className="adm-security-modal__row">
                            <span className="adm-security-modal__label">
                                Timestamp
                            </span>
                            <span className="adm-security-modal__value">
                                {fmtTs(timestamp)}
                            </span>
                        </div>
                    )}
                    <div className="adm-security-modal__row">
                        <span className="adm-security-modal__label">User</span>
                        <span className="adm-security-modal__value">
                            {entry.email || "Unknown"}
                        </span>
                    </div>
                    {entry.id && (
                        <div className="adm-security-modal__row">
                            <span className="adm-security-modal__label">
                                Recording
                            </span>
                            <span className="adm-security-modal__value">
                                {entry.question_slug ? (
                                    <Link
                                        to={`/dashboard/questions/${entry.question_slug}/recording/${entry.id}`}
                                    >
                                        {entry.id}
                                    </Link>
                                ) : (
                                    entry.id
                                )}
                            </span>
                        </div>
                    )}
                    {entry.question_slug && (
                        <div className="adm-security-modal__row">
                            <span className="adm-security-modal__label">
                                Question
                            </span>
                            <span className="adm-security-modal__value">
                                {entry.question_slug}
                            </span>
                        </div>
                    )}
                </div>
                <pre className="adm-security-modal__raw">
                    {JSON.stringify(entry, null, 2)}
                </pre>
                <div className="adm-security-modal__actions">
                    <button
                        type="button"
                        className="adm-security-btn adm-security-btn--ghost adm-security-btn--sm"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function ProcessingErrorsPanel({ errors, loading, onSelect }) {
    const rows = errors || [];
    return (
        <section className="adm-security-panel" aria-label="Processing errors">
            <SectionHead
                iconMod="lock"
                title="Processing errors"
                pill={{
                    label: `${rows.length} errors`,
                    mod:
                        rows.length > 0
                            ? "adm-security-pill--critical"
                            : "adm-security-pill--pass",
                }}
            />
            <div
                className="adm-security-errors-head"
                role="row"
                aria-label="Column headers"
            >
                <span role="columnheader">Timestamp</span>
                <span role="columnheader">User</span>
                <span role="columnheader">Recording</span>
                <span role="columnheader">Type</span>
                <span role="columnheader">Error</span>
            </div>
            <div
                className="adm-security-errors-body"
                role="table"
                aria-label="Processing errors"
            >
                {rows.length > 0 ? (
                    rows.map((entry, i) => (
                        <div
                            key={`${entry.id}-${i}`}
                            className="adm-security-errors-row"
                            role="row"
                            tabIndex={0}
                            style={{ "--i": i }}
                            onClick={() => onSelect?.(entry)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onSelect?.(entry);
                                }
                            }}
                        >
                            <span className="adm-security-errors-row__ts">
                                {fmtTs(entry.error_at || entry.created_at)}
                            </span>
                            <span className="adm-security-errors-row__user">
                                {entry.email || "Unknown"}
                            </span>
                            {entry.question_slug ? (
                                <Link
                                    to={`/dashboard/questions/${entry.question_slug}/recording/${entry.id}`}
                                    className="adm-security-errors-row__recording"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {entry.id}
                                </Link>
                            ) : (
                                <span className="adm-security-errors-row__recording">
                                    {entry.id}
                                </span>
                            )}
                            <span className="adm-security-errors-row__type">
                                {entry.type}
                            </span>
                            <span className="adm-security-errors-row__error">
                                {entry.message}
                            </span>
                        </div>
                    ))
                ) : loading ? (
                    <p className="adm-security-audit-empty">
                        Loading processing errors…
                    </p>
                ) : (
                    <p className="adm-security-audit-empty">
                        No processing errors detected.
                    </p>
                )}
            </div>
        </section>
    );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function AdministrationSecurity() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

    const [users, setUsers] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeAudit, setActiveAudit] = useState(null);
    const [activeProcessingError, setActiveProcessingError] = useState(null);

    const apiBase = API_BASE;

    const authedFetch = useCallback(
        async (path, options = {}) => {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            return fetch(`${apiBase}${path}`, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        [apiBase, getAccessTokenSilently]
    );

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            setLoading(false);
            setError("Log in to view security reporting.");
            return;
        }
        let aborted = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const [usersRes, analyticsRes] = await Promise.all([
                    authedFetch("/admin/users"),
                    authedFetch("/admin/analytics"),
                ]);
                if (!usersRes.ok) {
                    const t = await usersRes.text();
                    throw new Error(t || "Failed to load users.");
                }
                if (!analyticsRes.ok) {
                    const t = await analyticsRes.text();
                    throw new Error(t || "Failed to load analytics.");
                }
                const usersData = await usersRes.json();
                const analyticsData = await analyticsRes.json();
                if (aborted) return;
                setUsers(usersData.users || []);
                setAnalytics(analyticsData);
            } catch (err) {
                if (!aborted)
                    setError(
                        err?.message || "Failed to load security reporting."
                    );
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [authedFetch, isAuthenticated, isLoading]);

    const derived = useMemo(() => {
        if (!users.length && !analytics) {
            return {
                checks: [],
                auditLog: [],
                activeSessions: [],
                processingErrors: [],
            };
        }

        const now = Date.now();
        const admins = users.filter((u) => u.admin_role);
        const billingAdmins = admins.filter((u) => u.can_bill);
        const inactiveAdmins = admins.filter((u) => {
            if (!u.last_active_at) return false;
            const last = new Date(u.last_active_at).getTime();
            if (Number.isNaN(last)) return false;
            return now - last > 1000 * 60 * 60 * 24 * 30;
        });

        const processing = analytics?.processing_health || {};
        const pending =
            Number(processing.transcript_pending || 0) +
            Number(processing.feedback_pending || 0);
        const processingErrors = Number(processing.error_count || 0);
        const processingErrorRows = (processing.errors || []).map((row) => {
            const transcriptErr = row.transcript_error;
            const feedbackErr = row.feedback_error;
            const type =
                transcriptErr && feedbackErr
                    ? "Transcript + Feedback"
                    : transcriptErr
                      ? "Transcript"
                      : "Feedback";
            const message =
                transcriptErr && feedbackErr
                    ? `Transcript: ${transcriptErr} • Feedback: ${feedbackErr}`
                    : transcriptErr || feedbackErr || "Unknown error";
            return {
                ...row,
                type,
                message,
            };
        });

        const checks = [
            {
                id: "admins",
                label: "Admin coverage",
                status: admins.length > 0 ? "pass" : "warn",
                detail:
                    admins.length > 0
                        ? `${admins.length} admins with access`
                        : "No admins assigned",
            },
            {
                id: "inactive-admins",
                label: "Inactive admin access",
                status: inactiveAdmins.length > 0 ? "warn" : "pass",
                detail:
                    inactiveAdmins.length > 0
                        ? `${inactiveAdmins.length} admins inactive 30+ days`
                        : "All admins active in last 30 days",
            },
            {
                id: "billing",
                label: "Billing permissions",
                status: billingAdmins.length > 2 ? "warn" : "pass",
                detail: `${billingAdmins.length} admins can manage billing`,
            },
            {
                id: "processing-backlog",
                label: "Processing backlog",
                status: pending > 0 ? "warn" : "pass",
                detail:
                    pending > 0
                        ? `${pending} items awaiting processing`
                        : "No processing backlog",
            },
            {
                id: "processing-errors",
                label: "Processing errors",
                status: processingErrors > 0 ? "warn" : "pass",
                detail:
                    processingErrors > 0
                        ? `${processingErrors} errors in processing pipeline`
                        : "No processing errors",
            },
        ];

        const activeSessions = users
            .filter((u) => {
                if (!u.last_active_at) return false;
                const last = new Date(u.last_active_at).getTime();
                if (Number.isNaN(last)) return false;
                return now - last < 1000 * 60 * 60 * 24;
            })
            .map((u) => ({
                id: `session-${u.id}`,
                user: u.email,
                device: "Web browser",
                location: null,
                lastSeen: fmtRelative(u.last_active_at),
                current: false,
            }));

        const auditLog = users
            .flatMap((u) => {
                const rows = [];
                if (u.created_at) {
                    rows.push({
                        id: `created-${u.id}`,
                        actor: "system",
                        action: "Created account",
                        target: u.email,
                        severity: "info",
                        ts: u.created_at,
                    });
                }
                if (u.last_active_at) {
                    rows.push({
                        id: `active-${u.id}`,
                        actor: u.email,
                        action: "Recorded interview",
                        target: "practice session",
                        severity: "info",
                        ts: u.last_active_at,
                    });
                }
                return rows;
            })
            .sort((a, b) => new Date(b.ts) - new Date(a.ts))
            .slice(0, 50);

        if (processingErrors > 0) {
            auditLog.unshift({
                id: "processing-errors",
                actor: "system",
                action: "Processing errors detected",
                target: "pipeline",
                severity: "critical",
                ts: new Date().toISOString(),
            });
        }

        return {
            checks,
            auditLog,
            activeSessions,
            processingErrors: processingErrorRows,
        };
    }, [users, analytics]);

    const checksPass = derived.checks.filter((c) => c.status === "pass").length;
    const checksWarn = derived.checks.filter((c) => c.status === "warn").length;
    const criticalEvents = derived.auditLog.filter(
        (e) => e.severity === "critical"
    ).length;

    const securityChecks = derived.checks.filter(
        (c) => !c.id?.startsWith("processing-")
    );
    const opsChecks = derived.checks.filter((c) =>
        c.id?.startsWith("processing-")
    );

    return (
        <div className="adm-security" id="administration-security">
            <div className="adm-security__inner">
                <div>
                    <Breadcrumbs
                        items={[
                            { label: "Home", href: "/" },
                            {
                                label: "Administration",
                                href: "/administration",
                            },
                            {
                                label: "Security & compliance",
                                href: "/administration/security",
                            },
                        ]}
                    />

                    <div className="adm-security__header">
                        <div>
                            {/* <p className="adm-security__eyebrow">Administration</p> */}
                            <h1 className="adm-security__title">
                                Security &amp; compliance
                            </h1>
                            <p className="adm-security__sub">
                                Monitor access, audit events, and
                                organisation-wide security settings.
                            </p>
                        </div>
                        <div className="adm-security__header-actions">
                            <button
                                type="button"
                                className="adm-security-btn adm-security-btn--ghost"
                                onClick={() =>
                                    exportToCsv(
                                        derived.auditLog,
                                        "mooz-audit-log.csv"
                                    )
                                }
                                disabled={loading || !derived.auditLog.length}
                            >
                                <span
                                    className="adm-security-btn__icon adm-security-btn__icon--download"
                                    aria-hidden="true"
                                />
                                Export audit log
                            </button>
                        </div>
                    </div>
                </div>

                <div className="adm-security-stats-strip">
                    <StatCard
                        value={loading ? "—" : checksPass}
                        label="Checks passing"
                        sub={loading ? "Loading…" : `${checksWarn} need review`}
                        accent="green"
                    />
                    <StatCard
                        value={loading ? "—" : checksWarn}
                        label="Checks to review"
                        accent={checksWarn > 0 ? "warn" : "default"}
                    />
                    <StatCard
                        value={loading ? "—" : derived.activeSessions.length}
                        label="Active sessions"
                        sub="right now"
                        accent="default"
                    />
                    <StatCard
                        value={loading ? "—" : criticalEvents}
                        label="Critical events"
                        sub="last 7 days"
                        accent={criticalEvents > 0 ? "critical" : "default"}
                    />
                </div>

                {error && (
                    <p className="adm-security__error" role="alert">
                        {error}
                    </p>
                )}

                <SecurityChecksPanel
                    checks={opsChecks}
                    loading={loading}
                    title="Needs attention"
                    iconMod="activity"
                    ariaLabel="Operations triage checks"
                />

                <ProcessingErrorsPanel
                    errors={derived.processingErrors}
                    loading={loading}
                    onSelect={setActiveProcessingError}
                />

                <div className="adm-security-grid adm-security-grid--2">
                    <SecurityChecksPanel
                        checks={securityChecks}
                        loading={loading}
                        title="Security health"
                        iconMod="shield"
                        ariaLabel="Security health checks"
                    />
                    <ActiveSessionsPanel sessions={derived.activeSessions} />
                </div>

                <AuditLogPanel
                    log={derived.auditLog}
                    loading={loading}
                    onSelect={setActiveAudit}
                />

                <ProcessingErrorModal
                    entry={activeProcessingError}
                    onClose={() => setActiveProcessingError(null)}
                />

                <AuditLogModal
                    entry={activeAudit}
                    onClose={() => setActiveAudit(null)}
                />
            </div>
        </div>
    );
}
